import { AsyncCombobox } from 'compositions/AsyncCombobox'
import { type Column, DataGrid } from 'compositions/DataGrid'
import { Page } from 'compositions/Page'
import { useSends } from 'hooks/api'
import { useAsyncLoader } from 'hooks/useAsyncLoader'
import { X } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { Contact } from 'types/contacts'
import type { SendListRecord } from 'types/sends'

import { formatSendDate, recipientPreviewFromSummary, StatusBadge } from './send-shared'

function contactLabel(c: Contact): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || '(unnamed)'
  return c.email ? `${name} (${c.email})` : name
}

const COLUMNS: Column<SendListRecord>[] = [
  {
    key: 'source',
    header: 'Source',
    render: (row) => {
      if (row.scheduledCommunication) {
        return <span className="font-medium">{row.scheduledCommunication.name}</span>
      }
      return <span className="italic text-muted-foreground">(Transactional)</span>
    },
  },
  {
    key: 'template',
    header: 'Template',
    render: (row) => {
      if (row.template) {
        return <span>{row.template.name}</span>
      }
      return <span className="text-muted-foreground">-</span>
    },
  },
  {
    key: 'recipients',
    header: 'To',
    mobile: 'title',
    render: (row) => (
      <span className="text-sm">{recipientPreviewFromSummary(row.recipientsPreview, row.recipientCount)}</span>
    ),
  },
  {
    key: 'recipientCount',
    header: 'Recipients',
    mobile: 'hidden',
    sortable: true,
    render: (row) => (
      <span className="font-mono text-sm">
        {row.recipientCount}
        {row.failureCount > 0 && <span className="ml-1 text-xs text-destructive">({row.failureCount} failed)</span>}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    mobile: 'aside',
    render: (row) => <StatusBadge status={row.status} />,
  },
  {
    key: 'startedAt',
    header: 'Sent',
    sortable: true,
    render: (row) => <span className="whitespace-nowrap text-sm">{formatSendDate(row.startedAt)}</span>,
  },
]

export function CommunicationList() {
  const navigate = useNavigate()
  const [recipient, setRecipient] = useState<Contact | null>(null)
  const loadContacts = useAsyncLoader<Contact>('/api/contacts', { active: true })
  const { data: sends, loading } = useSends({ take: 100, contactId: recipient?.id })

  return (
    <Page>
      <div className="mb-4">
        <h1 className="text-xl font-semibold">Messages</h1>
        <p className="text-sm text-muted-foreground">Recent communication sends</p>
      </div>

      <div className="mb-4 space-y-2">
        <div className="w-full sm:max-w-sm">
          <AsyncCombobox<Contact>
            value={recipient}
            onChange={setRecipient}
            load={loadContacts}
            getKey={(c) => c.id}
            getLabel={contactLabel}
            getSublabel={(c) => c.email ?? c.phoneNumber ?? ''}
            placeholder="Filter by recipient…"
            searchPlaceholder="Search contacts by name, email, or phone…"
            emptyMessage="No contacts found"
            clearable
            clearLabel="Show all messages"
          />
        </div>
        {recipient && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground">
              <span className="shrink-0 opacity-60">Sent to:</span>
              <span className="truncate">{contactLabel(recipient)}</span>
              <button
                type="button"
                onClick={() => setRecipient(null)}
                aria-label="Clear recipient filter"
                className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-destructive/20 hover:text-destructive">
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        )}
      </div>

      <DataGrid
        columns={COLUMNS}
        data={sends ?? []}
        keyField="id"
        pageSize={20}
        loading={loading}
        storageKey="sends"
        emptyMessage={recipient ? 'No communications sent to this recipient.' : 'No data'}
        onRowClick={(row) => navigate(`/sends/${row.id}`)}
      />
    </Page>
  )
}
